import { useCallback } from 'react';
import { parseNamespacedLinkId } from './deepLink';
import { useNavigate } from 'react-router-dom';

// Shared notification-tap navigation resolver. Consumed by NotificationsPage.js
// and Topbar.js so the page-key-vs-URL branch (#44, #79, #98) is written once
// instead of drifting across independent implementations.
// Accepts either a full notification object ({ link_to, link_id, ... }) or a
// bare page-key string (Topbar's own literal onNavigate('notifications') calls).
// Maps a notification's link_to page key to which App-level "arrived via deep
// link" setter should receive its link_id. Different pages' link_id values
// live in different ID namespaces (an inventory_items.id vs. a
// material_requests.id) — never point two of them at the same setter. Add a
// new entry here, with its own dedicated setter threaded through App.js, when
// a new page starts consuming a notification's link_id. See Engineering
// Standards rule 14.
const ITEM_ID_DESTINATIONS = {
  requests_inbox: 'openRequestId',
  // #391 D11, Session 78n. Both keys address a material_requests row, so both
  // point at the same setter -- which is the one case the warning above allows,
  // because the namespace is identical rather than merely similar. Without this
  // entry the retargeted approved/denied notifications would open My requests
  // with nothing selected, which is a worse bug than the one D11 fixes: the
  // page would look like it had lost the request the notification named.
  my_requests: 'openRequestId',
  // #371 and #425, Session 79.6, gate decision D20. Its OWN setter, and this is
  // the whole point rather than tidiness.
  //
  // A branch_transfers id is a third namespace. Without this entry the resolver
  // below falls through to setSelectedItemId(), which is the INVENTORY ITEMS
  // namespace -- so "HOU-TRF-00001 has shipped" would have opened inventory item
  // 1. No error, no empty screen, just the wrong record looking entirely normal.
  //
  // That is the failure the comment above forbids in general terms and which
  // nothing had yet enforced, because until now only two page keys ever carried
  // a link_id. Acceptance line T13 runs it with a colliding id present.
  transfers: 'openTransferId',
};

// #605, Session 79.21c. A link id carrying the `transfer:` prefix is a
// branch_transfers row, wherever it is pointed. The Requests inbox is the only
// page that needs this today, because it is the only one showing two record
// types in one list — but the routing is keyed on the PREFIX rather than on the
// page, so a second such page cannot reintroduce the collision by forgetting to
// add itself here.
const NAMESPACED_DESTINATIONS = {
  transfer: 'openTransferRequestId',
};

// #682 -- WHAT A TRANSFER BELL OPENS. Houston's answer bell opens the transfer
// saying why, { id, alert: 'extra_answered' }, so its sheet shows S6's box
// (79.22b.6c.2b); the question's bell, { id, alert: 'extra_counted' }, opens
// Awaiting confirmation with the question card in view, not the transfer (the
// corrections gate's F3, 79.22b.6c.3). Every other bell: the id alone.
export function transferOpenTarget(notif, id) {
  const alert = notif?.event_type === 'transfer_extra_answered' ? 'extra_answered'
    : notif?.event_type === 'transfer_extra_counted' ? 'extra_counted' : null;
  return alert ? { id, alert } : id;
}

export function useNotificationNav(setActive, setSelectedItemId, setOpenRequestId, setOpenTransferId, setOpenTransferRequestId) {
  const navigate = useNavigate();

  return useCallback((target) => {
    const isNotifObject = target && typeof target === 'object';
    const linkTo = isNotifObject ? target.link_to : target;
    if (!linkTo) return;

    if (linkTo.startsWith('/')) {
      navigate(linkTo);
      return;
    }

    const linkId = isNotifObject ? target.link_id : null;
    if (linkId) {
      // #605 -- PARSE BEFORE ANYTHING ELSE TOUCHES THIS VALUE. Every branch
      // below used to parseInt() it, and parseInt('transfer:75', 10) is NaN --
      // which would reach a record lookup as a number and find nothing, or
      // worse, be handed to the inventory-items fallback. The parser returns a
      // real number or null, never NaN.
      const parsed = parseNamespacedLinkId(linkId);
      if (parsed && parsed.kind !== 'plain') {
        const namespaced = NAMESPACED_DESTINATIONS[parsed.kind];
        if (namespaced === 'openTransferRequestId' && setOpenTransferRequestId) {
          setOpenTransferRequestId(parsed.id);
        } else {
          // Same reasoning as the warn branch below: a namespaced id whose
          // setter is not threaded here must not fall through to a numeric
          // destination. Navigate without the record rather than opening the
          // wrong one.
          console.warn('[#605] no setter threaded for', parsed.kind, '- navigating without the record');
        }
        if (setActive) setActive(linkTo);
        return;
      }
      if (!parsed) {
        if (setActive) setActive(linkTo);
        return;
      }
      const destination = ITEM_ID_DESTINATIONS[linkTo];
      if (destination === 'openRequestId' && setOpenRequestId) {
        setOpenRequestId(parsed.id);
      } else if (destination === 'openTransferId' && setOpenTransferId) {
        // #682, Session 79.22b.6c.2b -- S6's box shows when the counting branch
        // opens the transfer FROM Houston's answer bell (the corrections gate's
        // F1), so that bell opens it saying why. Every other bell opens it with
        // the id alone, exactly as before.
        setOpenTransferId(transferOpenTarget(isNotifObject ? target : null, parsed.id));
      } else if (destination) {
        // A page key IS in the map but its setter was not threaded through by
        // this caller. Falling through to setSelectedItemId here would open an
        // inventory item with a foreign id -- the exact D20 collision -- so do
        // nothing with the id and still navigate. The page opens without a
        // record selected, which is honest; opening the WRONG record is not.
        console.warn('[#371] no setter threaded for', destination, '- navigating without the record');
      } else if (setSelectedItemId) {
        setSelectedItemId(parsed.id);
      }
    }
    if (setActive) setActive(linkTo);
  }, [navigate, setActive, setSelectedItemId, setOpenRequestId, setOpenTransferId, setOpenTransferRequestId]);
}